import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Building2,
  Mail,
  FolderKanban,
  CheckCircle2,
} from 'lucide-react';

interface OnboardingWizardProps {
  onComplete: () => void;
  onDismiss: () => void;
}

const TOTAL_STEPS = 4;

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onComplete,
  onDismiss,
}) => {
  const [currentStep, setCurrentStep] = useState(1);

  const goNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          aria-label="Dismiss onboarding wizard"
        >
          <X size={20} />
        </button>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pt-6 pb-2">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-200 ${
                i + 1 === currentStep
                  ? 'w-8 bg-blue-600 dark:bg-blue-500'
                  : i + 1 < currentStep
                  ? 'w-2 bg-blue-400 dark:bg-blue-400'
                  : 'w-2 bg-gray-200 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mb-2">
          Step {currentStep} of {TOTAL_STEPS}
        </p>

        {/* Step content */}
        <div className="px-8 pb-4">
          {currentStep === 1 && <StepWelcome />}
          {currentStep === 2 && <StepOrganization />}
          {currentStep === 3 && <StepEmail />}
          {currentStep === 4 && <StepProject />}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-8 pb-8 pt-2">
          <div>
            {currentStep > 1 ? (
              <button
                onClick={goBack}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ChevronLeft size={16} />
                Back
              </button>
            ) : (
              <button
                onClick={onDismiss}
                className="px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Skip
              </button>
            )}
          </div>

          <div>
            {currentStep < TOTAL_STEPS ? (
              <button
                onClick={goNext}
                className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={onComplete}
                className="flex items-center gap-1 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <CheckCircle2 size={16} />
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Individual Step Components ---------- */

const StepWelcome: React.FC = () => (
  <div className="text-center py-4">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-5">
      <Sparkles size={28} className="text-blue-600 dark:text-blue-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Welcome to WorkiDoc
    </h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mx-auto">
      WorkiDoc helps your organization manage correspondences, emails, and
      documents in one place. Let's walk through a few quick steps to get you
      started.
    </p>
    <ul className="mt-6 space-y-3 text-left max-w-xs mx-auto">
      {[
        'Centralize all your correspondences',
        'Connect email accounts for auto-sync',
        'Organize work into projects',
        'Track responses and approvals',
      ].map((item) => (
        <li
          key={item}
          className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"
        >
          <CheckCircle2
            size={16}
            className="text-green-500 mt-0.5 flex-shrink-0"
          />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const StepOrganization: React.FC = () => (
  <div className="text-center py-4">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center mb-5">
      <Building2 size={28} className="text-purple-600 dark:text-purple-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Set Up Your Organization
    </h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mx-auto">
      Your organization is the top-level workspace. Make sure its name and
      details are configured so your team can collaborate effectively.
    </p>
    <div className="mt-6">
      <Link
        to="/settings"
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
      >
        <Building2 size={16} />
        Go to Settings
      </Link>
    </div>
    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
      You can always update organization details later in Settings.
    </p>
  </div>
);

const StepEmail: React.FC = () => (
  <div className="text-center py-4">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mb-5">
      <Mail size={28} className="text-amber-600 dark:text-amber-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Connect an Email Account
    </h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mx-auto">
      Link a Gmail or Outlook account so WorkiDoc can automatically sync and
      classify your incoming correspondences.
    </p>
    <div className="mt-6">
      <Link
        to="/email-accounts"
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
      >
        <Mail size={16} />
        Go to Email Accounts
      </Link>
    </div>
    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
      You can skip this and connect email accounts at any time.
    </p>
  </div>
);

const StepProject: React.FC = () => (
  <div className="text-center py-4">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center mb-5">
      <FolderKanban size={28} className="text-green-600 dark:text-green-400" />
    </div>
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      Create Your First Project
    </h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mx-auto">
      Projects help you organize correspondences by department, client, or
      initiative. Create one to start grouping your documents.
    </p>
    <div className="mt-6">
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
      >
        <FolderKanban size={16} />
        Go to Projects
      </Link>
    </div>
    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
      You can create more projects later as your needs grow.
    </p>
  </div>
);

export default OnboardingWizard;
